import { lazy } from 'react';

import FormTemplateWithAuditHistory from '~/common/componentMapper/FormTemplate/FormTemplateWithAuditHistory';
import { RULE_ID, SUB_CATEGORY_IDS } from '~/common/constants';
import Taxes from '~/pages/Taxes/Taxes.js';

import TaxBreakdown from '../Taxes/TaxBreakdown.js';
import TaxesSummary from '../Taxes/TaxSummary.js';

const AppraisalWorkflowProvider = lazy(
  () => import('../Appraisals/workflows/AppraisalWorkflowProvider'),
);

const ContractReviewNoDocs = lazy(
  () => import('../PurchaseAgreement/ContractReviewNoDocs'),
);
const SmartContractReview = lazy(
  () => import('../PurchaseAgreement/SmartContractReview'),
);
const TitleCommitment = lazy(
  () => import('../TitlePackageReview/TitleCommitment'),
);
const SellerAcquisition = lazy(
  () => import('../TitlePackageReview/SellerAcquisition'),
);
const ClosingProtectionLetter = lazy(
  () => import('../TitlePackageReview/ClosingProtectionLetter'),
);
const WireInstructions = lazy(
  () => import('../TitlePackageReview/WireInstructions'),
);
const WireInstructionsV2 = lazy(
  () => import('../TitlePackageReview/WireInstructions/v2/WireInstructions.js'),
);

const TitleSummary = lazy(
  () => import('../TitlePackageReview/TracPlus/TitleSummary'),
);

const Borrower1003 = lazy(() => import('../Borrower1003'));
const Liabilities = lazy(() => import('../Liabilities/Liabilities'));
const MortgageHistory = lazy(
  () => import('~/pages/MortgageHistory/MortgageHistory.js'),
);
const MortgageHistoryV2 = lazy(
  () => import('~/pages/MortgageHistoryV2/MortgageHistoryV2.js'),
);
const EarlyClosingDisclosure = lazy(
  () => import('../EarlyClosingDisclosure/EarlyCD.js'),
);
const FinalChecks = lazy(() => import('../FinalReview/FinalChecks'));
const IncomeCalculator = lazy(() => import('../IncomeCalculator'));
const RentalIncome = lazy(() => import('../RentalIncome'));
const Reo = lazy(
  () =>
    import(
      '../REO/QuestionBuilderLiabilitiesREO/QuestionBuilderLiabilitiesREO'
    ),
);
const ThirdPartyChecks = lazy(
  () => import('../FinalReview/ThirdPartyChecks/ThirdPartyChecks'),
);
const ManualChecks = lazy(() => import('../FinalReview/ManualChecks'));
const SubjectProperty = lazy(() => import('../SubjectProperty'));
const AssetReconciliation = lazy(() => import('../Assets/AssetReconciliation'));
const TransferReconciliation = lazy(
  () => import('../Assets/TransferReconciliation'),
);
const AssetTypeAccount = lazy(() => import('../Assets'));
const FHARequirements = lazy(
  () => import('../PurchaseAgreement/FHARequirements'),
);
const NonOccupyingCoBorrower = lazy(() => import('../NonOccupyingCoBorrower'));
const UnderwriterFhaPage = lazy(() => import('../UnderwriterFha/Fha/FhaPage'));
const SecondaryFinancingPage = lazy(
  () => import('../UnderwriterFha/SecondaryFinancing/SecondaryFinancingPage'),
);
const UfmipRefundPage = lazy(
  () => import('../UnderwriterFha/UfmipRefund/UfmipRefundPage'),
);

const AppraisalNoDocsPage = lazy(
  () => import('../Appraisals/AppraisalNoDocsPage'),
);
const SubmissionSummaryReportPage = lazy(
  () => import('../Appraisals/SubmissionSummaryReportPage'),
);
const AppraisalNoUadPage = lazy(
  () => import('../Appraisals/AppraisalNoUadPage'),
);
const AppraisalHighestAndBestUsePage = lazy(
  () => import('../Appraisals/AppraisalHighestAndBestUsePage'),
);
const MarketPage = lazy(() => import('../Appraisals/MarketPage'));
const AssignmentInformationPage = lazy(
  () => import('../Appraisals/AssignmentInformationPage'),
);
const SubjectPropertyPage = lazy(
  () => import('../Appraisals/SubjectPropertyPage'),
);
const SitePage = lazy(() => import('../Appraisals/SitePage'));
const EnergyEfficientGreenFeaturesPage = lazy(
  () => import('../Appraisals/EnergyEfficientGreenFeaturesPage'),
);
const SubjectListingInformationPage = lazy(
  () => import('../Appraisals/SubjectListingInformationPage'),
);
const FunctionalObsolescencePage = lazy(
  () => import('../Appraisals/FunctionalObsolescencePage'),
);
const SalesContractPage = lazy(() => import('../Appraisals/SalesContractPage'));
const SalesComparisonApproachPage = lazy(
  () => import('../Appraisals/SalesComparisonApproachPage'),
);
const SubjectPropertyAmenitiesPage = lazy(
  () => import('../Appraisals/SubjectPropertyAmenitiesPage'),
);
const OverallConditionQualityPage = lazy(
  () => import('../Appraisals/OverallConditionQualityPage'),
);
const SketchPage = lazy(() => import('../Appraisals/SketchPage'));
const RemoveAppraisalPage = lazy(
  () => import('../Appraisals/RemoveAppraisalPage'),
);
const ManufacturedHomePage = lazy(
  () => import('../Appraisals/ManufacturedHomePage'),
);
const ProjectInformationPage = lazy(
  () => import('../Appraisals/ProjectInformationPage'),
);
const SupplementalInformationPage = lazy(
  () => import('../Appraisals/SupplementalInformationPage'),
);
const AppraisalReconciliationPage = lazy(
  () => import('../Appraisals/AppraisalReconciliationPage'),
);
const RentalInformationPage = lazy(
  () => import('../Appraisals/RentalInformationPage'),
);
const AppraisalAlternativeEligibilityPage = lazy(
  () => import('../Appraisals/AppraisalAlternativeEligibilityPage'),
);
const IncomeApproachPage = lazy(
  () => import('../Appraisals/IncomeApproachPage'),
);
const CostApproachPage = lazy(() => import('../Appraisals/CostApproachPage'));
const FhaRequirementsPage = lazy(
  () => import('../Appraisals/FhaRequirementsPage'),
);

const UnitInteriorPage = lazy(() => import('../Appraisals/UnitInteriorPage'));
const VehicleStoragePage = lazy(
  () => import('../Appraisals/VehicleStoragePage'),
);
const DwellingExteriorPage = lazy(
  () => import('../Appraisals/DwellingExteriorPage'),
);
const OutbuildingPage = lazy(() => import('../Appraisals/OutbuildingPage'));
const Generic = lazy(() => import('../Generic'));
const Oops = lazy(() => import('~/common/components/Oops'));

const UWFinalReview = lazy(
  () => import('../FinalReview/UWFinalReview/UWFinalReview'),
);

const UWStatusAndReview = lazy(
  () => import('../FinalReview/UWFinalReview/ReviewStatus'),
);

const HomeOwnerInsurance = lazy(
  () =>
    import(
      '../SubjectProperty/Insurance/HomeOwnerInsurance/HomeOwnerInsurance.js'
    ),
);

const SubjectPropertyOccupancy = lazy(
  () => import('../SubjectPropertyOccupancy/FHASubjectPropertyOccupancy'),
);

const MissingPayee = lazy(() => import('../Taxes/MissingPayee'));

const Derogatory = lazy(() => import('../Credit/Credit'));

const LoanIdentificationNumber = lazy(
  () => import('../VA/LoanIdentificationNumber/LoanIdentificationNumber'),
);

const VaBorrowerNTB = lazy(() => import('../Borrower1003/VaBorrowerNTB'));

const Obligor = lazy(() => import('../VA/Obligor/Obligor'));

const LinNoDocs = lazy(() => import('../VA/LinNoDocs/LinNoDocs'));

const NewConstructionRequirements = lazy(
  () => import('../NewConstructionRequirements/NewConstructionRequirements'),
);

const Caivrs = lazy(() => import('../VA/Caivrs/Caivrs'));

const VANote = lazy(() => import('../VA/DocumentReview/Note/NotePage'));
const VANoteNoDocs = lazy(() => import('../VA/DocumentReview/Note/NoteNoDocs'));

const VACreditReport = lazy(
  () => import('../VA/DocumentReview/CreditReport/CreditReportPage'),
);

const VACreditSupplement = lazy(
  () => import('../VA/DocumentReview/CreditSupplement/CreditSupplementPage'),
);

const VAMortgageStatement = lazy(
  () => import('../VA/DocumentReview/MortgageStatement/MortgageStatementPage'),
);

const VAForbearanceReview = lazy(
  () => import('../VA/DocumentReview/ForbearanceReview/ForbearanceReviewPage'),
);

const VAPayoff = lazy(
  () => import('../VA/DocumentReview/Payoff/PayoffPage.js'),
);
const VAPayoffNoDocs = lazy(
  () => import('../VA/DocumentReview/Payoff/PayoffNoDocs.js'),
);

const VALoanModificationAgreement = lazy(
  () =>
    import(
      '../VA/DocumentReview/LoanModificationAgreement/LoanModificationAgreementPage'
    ),
);

const VALoanModificationAgreementNoDocs = lazy(
  () =>
    import(
      '../VA/DocumentReview/LoanModificationAgreement/LoanModificationAgreementNoDocs'
    ),
);

const CaivrsNoDocs = lazy(() => import('../VA/Caivrs/CaivrsNoDocs'));

const SUB_CATEGORY_COMPONENTS = {
  [SUB_CATEGORY_IDS.CREDIT]: <Generic />,
  [SUB_CATEGORY_IDS.CREDIT_LIABILITIES]: <Liabilities />,
  [SUB_CATEGORY_IDS.VA_LIABILITIES]: <Liabilities />,
  [SUB_CATEGORY_IDS.NET_TANGIBLE_BENEFITS]: <Generic />,
  [SUB_CATEGORY_IDS.INCOME_CALCULATOR]: <IncomeCalculator />,
  [SUB_CATEGORY_IDS.BORROWER1003]: <Borrower1003 />,
  [SUB_CATEGORY_IDS.REO]: <Reo />,
  [SUB_CATEGORY_IDS.THIRD_PARTY_CHECKS_]: <ThirdPartyChecks />,
  [SUB_CATEGORY_IDS.RENTAL_INCOME]: <RentalIncome />,
  [SUB_CATEGORY_IDS.FINAL_CHECKS]: <FinalChecks />,
  [SUB_CATEGORY_IDS.EARLY_CLOSING_DISCLOSURE]: <EarlyClosingDisclosure />,
  [SUB_CATEGORY_IDS.MANUAL_CHECKS]: <ManualChecks />,
  [SUB_CATEGORY_IDS.SUBJECT_PROPERTY]: <SubjectProperty />,
  [SUB_CATEGORY_IDS.TITLE]: <TitleCommitment />,
  [SUB_CATEGORY_IDS.NON_OCCUPYING_CO_BORROWERS]: <NonOccupyingCoBorrower />,
  [SUB_CATEGORY_IDS.HOMEOWNERS_INSURANCE]: <HomeOwnerInsurance />,
  [SUB_CATEGORY_IDS.NEW_CONSTRUCTION_REQUIREMENTS]: (
    <NewConstructionRequirements />
  ),
};

type CategoryToDisplayProps = {
  subId: number;
  ruleId: number;
  additionalReferences: any;
  isUnderwriter: boolean;
};

export const CategoryToDisplay = ({
  subId,
  ruleId,
  additionalReferences,
  isUnderwriter,
}: Readonly<CategoryToDisplayProps>) => {
  if (
    subId === SUB_CATEGORY_IDS.CREDIT ||
    subId === SUB_CATEGORY_IDS.CREDIT_LIABILITIES
  ) {
    return (
      <CreditContainer
        ruleId={ruleId}
        additionalReferences={additionalReferences}
        subId={subId}
      />
    );
  }

  if (subId === SUB_CATEGORY_IDS.FINAL_UNDERWRITE_REVIEW) {
    return <FinalReviewPage ruleId={ruleId} />;
  }

  if (subId === SUB_CATEGORY_IDS.UNDERWRITER_FHA) {
    return <UnderwriterFhaContainer ruleId={ruleId} />;
  }

  if (
    subId === SUB_CATEGORY_IDS.PURCHASE_AGREEMENT ||
    subId === SUB_CATEGORY_IDS.FHA_REQUIREMENTS
  ) {
    return <PurchaseAgreementAndFHARequirementsContainer ruleId={ruleId} />;
  }

  if (subId === SUB_CATEGORY_IDS.ASSETS) {
    return <AssetsContainer ruleId={ruleId} />;
  }

  if (subId === SUB_CATEGORY_IDS.TITLE) {
    return (
      <TitleContainer
        ruleId={ruleId}
        additionalReferences={additionalReferences}
      />
    );
  }

  if (
    subId === SUB_CATEGORY_IDS.HOMEOWNERS_INSURANCE ||
    ruleId === RULE_ID.SUPPLEMENTAL_PARENT_RULE_ID
  ) {
    return <HomeOwnerInsurance />;
  }

  if (subId === SUB_CATEGORY_IDS.NET_TANGIBLE_BENEFITS) {
    const formTemplate = isUnderwriter
      ? FormTemplateWithAuditHistory
      : undefined;

    return <Generic formTemplate={formTemplate} />;
  }

  if (subId === SUB_CATEGORY_IDS.VA_BORROWER) {
    return <VABorrowerContainer ruleId={ruleId} />;
  }

  if (subId === SUB_CATEGORY_IDS.INCOME_CALCULATOR) {
    return <IncomeCalculator />;
  }

  if (subId === SUB_CATEGORY_IDS.VA) {
    return (
      <VAContainer
        ruleId={ruleId}
        additionalReferences={additionalReferences}
      />
    );
  }

  // Check for tax-related rules
  const taxComponent = getTaxComponent(ruleId, additionalReferences);
  if (taxComponent) {
    return taxComponent;
  }

  // Check for appraisal-related rules
  const appraisalsComponent = getAppraisalsComponent(
    ruleId,
    additionalReferences,
  );
  if (appraisalsComponent) {
    return appraisalsComponent;
  }

  // Use SUB_CATEGORY_COMPONENTS for default rendering, fallback to Oops if not found
  const Component = SUB_CATEGORY_COMPONENTS[subId];
  return Component || <Oops />;
};

const getTaxComponent = (
  ruleId: number,
  additionalReferences: any,
): JSX.Element | null => {
  if (ruleId === RULE_ID.TAX_SUMMARY_RULE_ID) {
    return <TaxesSummary key={ruleId} />;
  }

  if (ruleId === RULE_ID.TAX_ESCROW_DETAILS_RULE_ID) {
    return (
      <Taxes
        key={additionalReferences.TaxId}
        taxId={additionalReferences.TaxId}
      />
    );
  }

  if (ruleId === RULE_ID.MISSING_PAYEE_RULE_ID) {
    return <MissingPayee taxId={additionalReferences.TaxId} />;
  }

  if (
    ruleId === RULE_ID.TAX_BREAKDOWN_RULE_ID ||
    ruleId === RULE_ID.TAX_BREAKDOWN_NO_DOCS_RULE_ID
  ) {
    return <TaxBreakdown />;
  }

  return null;
};

const APPRAISAL_COMPONENTS: Record<
  number,
  (appraiserLicenseNumber: string, additionalReferences: any) => JSX.Element
> = {
  [RULE_ID.APPRAISALS_HIGHEST_AND_BEST_USE_RULE_ID]: (
    appraiserLicenseNumber,
  ) => (
    <AppraisalHighestAndBestUsePage
      appraiserLicenseNumber={appraiserLicenseNumber}
    />
  ),
  [RULE_ID.APPRAISALS_MARKET_RULE_ID]: (appraiserLicenseNumber) => (
    <MarketPage appraiserLicenseNumber={appraiserLicenseNumber} />
  ),
  [RULE_ID.APPRAISALS_NO_DOCS_RULE_ID]: () => <AppraisalNoDocsPage />,
  [RULE_ID.APPRAISALS_NO_UAD_RULE_ID]: () => <AppraisalNoUadPage />,
  [RULE_ID.APPRAISALS_SSR_RULE_ID]: () => <SubmissionSummaryReportPage />,
  [RULE_ID.APPRAISALS_RECONCILIATION_RULE_ID]: () => (
    <AppraisalReconciliationPage />
  ),
  [RULE_ID.APPRAISALS_ALTERNATIVE_ELIGIBILITY_RULE_ID]: () => (
    <AppraisalAlternativeEligibilityPage />
  ),
  [RULE_ID.APPRAISALS_SUBJECT_PROPERTY_RULE_ID]: (appraiserLicenseNumber) => (
    <SubjectPropertyPage appraiserLicenseNumber={appraiserLicenseNumber} />
  ),
  [RULE_ID.APPRAISALS_RULE_ID]: (appraiserLicenseNumber) => (
    <AssignmentInformationPage
      appraiserLicenseNumber={appraiserLicenseNumber}
    />
  ),
  [RULE_ID.APPRAISALS_REMOVE_APPRAISAL_RULE_ID]: (appraiserLicenseNumber) => (
    <RemoveAppraisalPage appraiserLicenseNumber={appraiserLicenseNumber} />
  ),
  [RULE_ID.APPRAISALS_SITE_RULE_ID]: (appraiserLicenseNumber) => (
    <SitePage appraiserLicenseNumber={appraiserLicenseNumber} />
  ),
  [RULE_ID.APPRAISALS_ENERGY_RULE_ID]: (appraiserLicenseNumber) => (
    <EnergyEfficientGreenFeaturesPage
      appraiserLicenseNumber={appraiserLicenseNumber}
    />
  ),
  [RULE_ID.APPRAISALS_CONDITION_QUALITY_RULE_ID]: (appraiserLicenseNumber) => (
    <OverallConditionQualityPage
      appraiserLicenseNumber={appraiserLicenseNumber}
    />
  ),
  [RULE_ID.APPRAISALS_SKETCH_RULE_ID]: (appraiserLicenseNumber) => (
    <SketchPage appraiserLicenseNumber={appraiserLicenseNumber} />
  ),
  [RULE_ID.APPRAISALS_MANUFACTURED_HOME_RULE_ID]: (appraiserLicenseNumber) => (
    <ManufacturedHomePage appraiserLicenseNumber={appraiserLicenseNumber} />
  ),
  [RULE_ID.APPRAISALS_PROJECT_INFORMATION_RULE_ID]: (
    appraiserLicenseNumber,
  ) => (
    <ProjectInformationPage appraiserLicenseNumber={appraiserLicenseNumber} />
  ),
  [RULE_ID.APPRAISALS_SUBJECT_LISTING_INFO_RULE_ID]: (
    appraiserLicenseNumber,
  ) => (
    <SubjectListingInformationPage
      appraiserLicenseNumber={appraiserLicenseNumber}
    />
  ),
  [RULE_ID.APPRAISALS_FUNCTIONAL_OBSOLESCENCE_RULE_ID]: (
    appraiserLicenseNumber,
  ) => (
    <FunctionalObsolescencePage
      appraiserLicenseNumber={appraiserLicenseNumber}
    />
  ),
  [RULE_ID.APPRAISALS_UNIT_INTERIOR_RULE_ID]: (
    appraiserLicenseNumber,
    additionalReferences,
  ) => (
    <UnitInteriorPage
      key={additionalReferences?.UnitIdentifier ?? ''}
      appraiserLicenseNumber={appraiserLicenseNumber}
      unitIdentifier={additionalReferences?.UnitIdentifier ?? ''}
    />
  ),
  [RULE_ID.APPRAISALS_SUPPLEMENTAL_INFORMATION_RULE_ID]: (
    appraiserLicenseNumber,
  ) => (
    <SupplementalInformationPage
      appraiserLicenseNumber={appraiserLicenseNumber}
    />
  ),
  [RULE_ID.APPRAISALS_SALES_CONTRACT_RULE_ID]: (appraiserLicenseNumber) => (
    <SalesContractPage appraiserLicenseNumber={appraiserLicenseNumber} />
  ),
  [RULE_ID.APPRAISALS_SALES_COMPARISON_APPROACH_RULE_ID]: (
    appraiserLicenseNumber,
  ) => (
    <SalesComparisonApproachPage
      appraiserLicenseNumber={appraiserLicenseNumber}
    />
  ),
  [RULE_ID.APPRAISALS_RENTAL_INFORMATION_RULE_ID]: (appraiserLicenseNumber) => (
    <RentalInformationPage appraiserLicenseNumber={appraiserLicenseNumber} />
  ),
  [RULE_ID.APPRAISALS_INCOME_APPROACH_RULE_ID]: (appraiserLicenseNumber) => (
    <IncomeApproachPage appraiserLicenseNumber={appraiserLicenseNumber} />
  ),
  [RULE_ID.APPRAISALS_COST_APPROACH_RULE_ID]: (appraiserLicenseNumber) => (
    <CostApproachPage appraiserLicenseNumber={appraiserLicenseNumber} />
  ),
  [RULE_ID.APPRAISALS_FHA_REQUIREMENTS_RULE_ID]: (appraiserLicenseNumber) => (
    <FhaRequirementsPage appraiserLicenseNumber={appraiserLicenseNumber} />
  ),
  [RULE_ID.APPRAISALS_SUBJECT_PROPERTY_AMENITIES_RULE_ID]: (
    appraiserLicenseNumber,
  ) => (
    <SubjectPropertyAmenitiesPage
      appraiserLicenseNumber={appraiserLicenseNumber}
    />
  ),
  [RULE_ID.APPRAISALS_VEHICLE_STORAGE_RULE_ID]: (appraiserLicenseNumber) => (
    <VehicleStoragePage appraiserLicenseNumber={appraiserLicenseNumber} />
  ),
  [RULE_ID.APPRAISALS_DWELLING_EXTERIOR_RULE_ID]: (
    appraiserLicenseNumber,
    additionalReferences,
  ) => (
    <DwellingExteriorPage
      key={additionalReferences?.UnitIdentifier ?? ''}
      appraiserLicenseNumber={appraiserLicenseNumber}
      structureIdentifier={additionalReferences?.UnitIdentifier ?? ''}
    />
  ),
  [RULE_ID.APPRAISALS_OUTBUILDING_RULE_ID]: (
    appraiserLicenseNumber,
    additionalReferences,
  ) => (
    <OutbuildingPage
      key={additionalReferences?.OutbuildingType ?? ''}
      appraiserLicenseNumber={appraiserLicenseNumber}
      outbuildingType={additionalReferences?.OutbuildingType ?? ''}
    />
  ),
};

const getAppraisalsComponent = (
  ruleId: number,
  additionalReferences: any,
): JSX.Element | null => {
  const appraiserLicenseNumber =
    additionalReferences?.AppraiserLicenseNumber ?? '';
  const createComponent = APPRAISAL_COMPONENTS[ruleId];
  if (!createComponent) {
    return null;
  }

  return (
    <AppraisalWorkflowProvider>
      {createComponent(appraiserLicenseNumber, additionalReferences)}
    </AppraisalWorkflowProvider>
  );
};

const FinalReviewPage = ({
  ruleId,
}: Readonly<Partial<CategoryToDisplayProps>>) => {
  switch (ruleId) {
    case RULE_ID.STATUS_AND_REVIEW_FLAG_RULE_ID:
      return <UWStatusAndReview />;
    case RULE_ID.FINAL_REVIEW_RULE_ID:
      return <UWFinalReview />;
    default:
      return null;
  }
};

const CreditContainer = ({
  ruleId,
  additionalReferences,
  subId,
}: Readonly<Partial<CategoryToDisplayProps>>) => {
  switch (true) {
    case ruleId === RULE_ID.DEROGATORY_EVENTS_PARENT_RULE &&
      additionalReferences?.IsEnhancedFHADisputedAccountsEnabled === 'true':
      return <Derogatory />;
    case ruleId === RULE_ID.MORTGAGE_HISTORY_ALL_ALERTS_RULE_V2:
      return <MortgageHistoryV2 />;
    case ruleId === RULE_ID.MORTGAGE_HISTORY_PARENT_RULE:
      return <MortgageHistory />;
    case ruleId === RULE_ID.CREDIT_REPORT_MANUAL_REVIEW:
    case ruleId === RULE_ID.LIABILITY_ALL_ALERTS_RULE &&
      additionalReferences?.IsLiabilitiesReviewSduiEnabled === 'True':
    case ruleId === RULE_ID.RECENTLY_PAID_OFF_DEBT_PARENT_RULE:
      return <Generic />;
    default:
      return subId === SUB_CATEGORY_IDS.CREDIT ? <Generic /> : <Liabilities />;
  }
};

const UnderwriterFhaContainer = ({
  ruleId,
}: Readonly<Partial<CategoryToDisplayProps>>) => {
  switch (ruleId) {
    case RULE_ID.UNDERWRITER_FHA_RULE_ID:
      return <UnderwriterFhaPage />;
    case RULE_ID.FHA_UFMIP_REFUND_RULE_ID:
      return <UfmipRefundPage />;
    case RULE_ID.FHA_SECONDARY_FINANCING_RULE_ID:
      return <SecondaryFinancingPage />;
  }
};

const PurchaseAgreementAndFHARequirementsContainer = ({
  ruleId,
}: Readonly<Partial<CategoryToDisplayProps>>) => {
  switch (ruleId) {
    case RULE_ID.FHA_REQUIREMENTS:
      return <FHARequirements />;
    case RULE_ID.PURCHASE_AGREEMENT_RULE_ID:
      return <SmartContractReview />;
    case RULE_ID.PURCHASE_AGREEMENT_NO_DOCS_RULE_ID:
      return <ContractReviewNoDocs />;
    default:
      return null;
  }
};

const AssetsContainer = ({
  ruleId,
}: Readonly<Partial<CategoryToDisplayProps>>) => {
  switch (ruleId) {
    case RULE_ID.ASSET_RECONCILIATION_RULE_ID:
      return <AssetReconciliation />;
    case RULE_ID.TRANSFER_RECONCILIATION_RULE_ID:
      return <TransferReconciliation />;
    default:
      return <AssetTypeAccount />;
  }
};

const TitleContainer = ({
  ruleId,
  additionalReferences,
}: Readonly<Partial<CategoryToDisplayProps>>) => {
  switch (ruleId) {
    case RULE_ID.SELLER_ACQUISITION_RULE:
      return <SellerAcquisition />;
    case RULE_ID.TITLE_COMMITMENT_RULE:
      return <TitleCommitment />;
    case RULE_ID.CPL_REVIEW_RULE:
      return <ClosingProtectionLetter />;
    case RULE_ID.WIRE_INSTRUCTIONS_RULE:
      if (additionalReferences?.IsWireInstructionsRefactorEnabled === 'true') {
        return <WireInstructionsV2 />;
      }

      return <WireInstructions />;
    case RULE_ID.SUBJECT_PROPERTY_OCCUPANCY_FHA_CASHOUT_RULE_ID:
      return <SubjectPropertyOccupancy />;
    case RULE_ID.TITLE_SUMMARY_RULE_ID:
      return <TitleSummary />;
    default:
      return <TitleCommitment />;
  }
};

const VABorrowerContainer = ({
  ruleId,
}: Readonly<Partial<CategoryToDisplayProps>>) => {
  switch (ruleId) {
    case RULE_ID.NET_TANGIBLE_BENEFITS_VA_IRRRL_RULE_ID:
      return <VaBorrowerNTB />;
    case RULE_ID.VA_OBLIGOR_RULE_ID:
      return <Obligor />;
    case RULE_ID.VA_OBLIGOR_NO_DOCS_RULE_ID:
      return <LinNoDocs displayTitle="Obligor" />;
    default:
      return null;
  }
};

const VAContainer = ({
  ruleId,
  additionalReferences,
}: Readonly<Partial<CategoryToDisplayProps>>) => {
  switch (ruleId) {
    case RULE_ID.VA_LOAN_IDENTIFICATION_NUMBER_RULE_ID:
      return <LoanIdentificationNumber />;
    case RULE_ID.VA_CAIVRS_RULE_ID:
      return <Caivrs />;
    case RULE_ID.VA_LOAN_IDENTIFICATION_NUMBER_NO_DOCS_RULE_ID:
      return <LinNoDocs displayTitle="Loan Identification Number" />;
    case RULE_ID.VA_DOCUMENT_REVIEW_NOTE_RULE_ID:
      return <VANote />;
    case RULE_ID.VA_DOCUMENT_REVIEW_NOTE_NO_DOCS_RULE_ID:
      return <VANoteNoDocs />;
    case RULE_ID.VA_DOCUMENT_REVIEW_CREDIT_REPORT_RULE_ID:
      return <VACreditReport />;
    case RULE_ID.VA_DOCUMENT_REVIEW_CREDIT_SUPPLEMENT_RULE_ID:
      return <VACreditSupplement />;
    case RULE_ID.VA_DOCUMENT_REVIEW_MORTGAGE_STATEMENT_RULE_ID:
      return (
        <VAMortgageStatement
          key={additionalReferences?.DocumentId ?? ''}
          documentId={+(additionalReferences?.DocumentId ?? 0)}
        />
      );
    case RULE_ID.VA_DOCUMENT_REVIEW_FORBEARANCE_REVIEW_RULE_ID:
      return <VAForbearanceReview />;
    case RULE_ID.VA_DOCUMENT_REVIEW_PAYOFF_STATEMENT_RULE_ID:
      return <VAPayoff />;
    case RULE_ID.VA_DOCUMENT_REVIEW_PAYOFF_STATEMENT_NO_DOCS_RULE_ID:
      return <VAPayoffNoDocs displayTitle="Payoff" />;
    case RULE_ID.VA_DOCUMENT_REVIEW_LOAN_MODIFICATION_AGREEMENT_RULE_ID:
      return <VALoanModificationAgreement />;
    case RULE_ID.VA_DOCUMENT_REVIEW_LOAN_MODIFICATION_AGREEMENT_NO_DOCS_RULE_ID:
      return <VALoanModificationAgreementNoDocs />;
    case RULE_ID.VA_CAIVRS_NO_DOCS_RULE_ID:
      return <CaivrsNoDocs />;
    default:
      return null;
  }
};
